import ColorThief from 'colorthief';

const DEFAULT_COLOR = '#FF3496';
const MAX_RETRIES = 2;
const RETRY_DELAY = 300; // ms
const MAX_CACHE_SIZE = 200;
const MAX_CANVAS_SIZE = 200; // Sufficient for color analysis

// ============================================================================
// TypeScript Type Definitions
// ============================================================================

type RGBColor = [number, number, number];

interface ColorThiefInstance {
  getColor(img: HTMLImageElement | HTMLCanvasElement | null, quality?: number): RGBColor;
  getPalette(
    img: HTMLImageElement | HTMLCanvasElement | null,
    colorCount?: number,
    quality?: number,
  ): RGBColor[];
}

enum LogLevel {
  WARN = 'warn',
  ERROR = 'error',
  INFO = 'info',
}

// ============================================================================
// LRU Cache Implementation
// ============================================================================

class LRUCache<K, V> {
  private cache = new Map<K, V>();
  private maxSize: number;

  constructor(maxSize: number = 100) {
    this.maxSize = maxSize;
  }

  get(key: K): V | undefined {
    const value = this.cache.get(key);
    if (value !== undefined) {
      // Move to end (most recent)
      this.cache.delete(key);
      this.cache.set(key, value);
    }
    return value;
  }

  set(key: K, value: V): void {
    // Remove if exists to update position
    if (this.cache.has(key)) {
      this.cache.delete(key);
    }
    // Remove oldest if at capacity
    else if (this.cache.size >= this.maxSize) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey !== undefined) {
        this.cache.delete(firstKey);
      }
    }
    this.cache.set(key, value);
  }

  has(key: K): boolean {
    return this.cache.has(key);
  }

  clear(): void {
    this.cache.clear();
  }

  get size(): number {
    return this.cache.size;
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Convert RGB array to hex color string
 */
function rgbToHex(rgb: RGBColor): string {
  const hexString = rgb
    .map((x: number) => {
      const hex = x.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    })
    .join('');
  return `#${hexString.toUpperCase()}`;
}

/**
 * Extract S3 key from presigned URL for caching
 */
function extractS3KeyFromPresignedUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    // Remove leading slash and query parameters
    const pathname = urlObj.pathname.substring(1);
    // Handle bucket-in-path style: /bucket-name/key
    const parts = pathname.split('/');
    // If first part looks like a bucket name, skip it
    if (parts.length > 1 && parts[0].includes('.s3')) {
      return parts.slice(1).join('/');
    }
    return pathname || url;
  } catch {
    return url; // URL parsing failed, use entire URL as key
  }
}

/**
 * Standardized logging utility
 */
function logAnalysisIssue(message: string, level: LogLevel = LogLevel.WARN) {
  if (process.env.NODE_ENV === 'development') {
    console[level]('[Image Analysis]', message);
  }
}

/**
 * Check browser compatibility for required APIs
 */
function isBrowserCompatible(): boolean {
  try {
    return !!(
      typeof window !== 'undefined' &&
      window.Image &&
      window.URL &&
      typeof URL.createObjectURL === 'function' &&
      typeof URL.revokeObjectURL === 'function' &&
      document.createElement('canvas').getContext
    );
  } catch {
    return false;
  }
}

// ============================================================================
// Cache and Pending Promises Management
// ============================================================================

const colorCache = new LRUCache<string, string>(MAX_CACHE_SIZE);
const pendingAnalyses = new Map<string, Promise<void>>();

// ============================================================================
// Image Color Analysis
// ============================================================================

// 영역 분석 설정 (변경 가능)
const REGION_CONFIG = {
  TOP_START: 0.08, // 상단 영역 시작 (상위 5%부터)
  TOP_END: 0.22, // 상단 영역 끝 (상위 20%까지)
  BOTTOM_START: 0.82, // 하단 영역 시작 (하위 15%부터)
  BOTTOM_END: 0.93, // 하단 영역 끝 (하위 5%까지)
} as const;

/**
 * Canvas에서 직접 dominant 색상 추출
 */
function getDominantColorFromCanvas(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  k: number = 5,
): string {
  try {
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = imageData.data;

    // 픽셀 샘플링 (10픽셀마다)
    const samples: [number, number, number][] = [];
    for (let i = 0; i < pixels.length; i += 40) {
      const r = pixels[i];
      const g = pixels[i + 1];
      const b = pixels[i + 2];
      const a = pixels[i + 3];

      if (a >= 128) {
        samples.push([r, g, b]);
      }
    }

    if (samples.length === 0) return DEFAULT_COLOR;

    // 랜덤 초기 중심점
    const centroids: [number, number, number][] = [];
    for (let i = 0; i < k; i++) {
      centroids.push(samples[Math.floor(Math.random() * samples.length)]);
    }

    // K-Means 반복 (최대 10회)
    for (let iter = 0; iter < 10; iter++) {
      const clusters: [number, number, number][][] = Array(k)
        .fill(null)
        .map(() => []);

      // 각 픽셀을 가장 가까운 중심점에 할당
      for (const sample of samples) {
        let minDist = Infinity;
        let minIdx = 0;

        for (let i = 0; i < k; i++) {
          const dist = Math.sqrt(
            Math.pow(sample[0] - centroids[i][0], 2) +
              Math.pow(sample[1] - centroids[i][1], 2) +
              Math.pow(sample[2] - centroids[i][2], 2),
          );

          if (dist < minDist) {
            minDist = dist;
            minIdx = i;
          }
        }

        clusters[minIdx].push(sample);
      }

      // 새로운 중심점 계산
      for (let i = 0; i < k; i++) {
        if (clusters[i].length > 0) {
          const avgR = clusters[i].reduce((sum, p) => sum + p[0], 0) / clusters[i].length;
          const avgG = clusters[i].reduce((sum, p) => sum + p[1], 0) / clusters[i].length;
          const avgB = clusters[i].reduce((sum, p) => sum + p[2], 0) / clusters[i].length;
          centroids[i] = [avgR, avgG, avgB];
        }
      }
    }

    // 가장 큰 클러스터의 중심점 반환
    let maxSize = 0;
    let dominantCentroid: [number, number, number] = [128, 128, 128];

    const clusters: [number, number, number][][] = Array(k)
      .fill(null)
      .map(() => []);
    for (const sample of samples) {
      let minDist = Infinity;
      let minIdx = 0;

      for (let i = 0; i < k; i++) {
        const dist = Math.sqrt(
          Math.pow(sample[0] - centroids[i][0], 2) +
            Math.pow(sample[1] - centroids[i][1], 2) +
            Math.pow(sample[2] - centroids[i][2], 2),
        );

        if (dist < minDist) {
          minDist = dist;
          minIdx = i;
        }
      }

      clusters[minIdx].push(sample);
    }

    for (let i = 0; i < k; i++) {
      if (clusters[i].length > maxSize) {
        maxSize = clusters[i].length;
        dominantCentroid = centroids[i];
      }
    }

    return rgbToHex([
      Math.round(dominantCentroid[0]),
      Math.round(dominantCentroid[1]),
      Math.round(dominantCentroid[2]),
    ]);
  } catch (error) {
    console.error('Error extracting color from canvas:', error);
    return DEFAULT_COLOR;
  }
}

/**
 * Analyze color from presigned URL image with regional analysis
 * Two-tier approach: Direct load → Fetch fallback
 * @param presignedUrl S3 presigned URL
 * @param topCallback Top region color extraction callback
 * @param bottomCallback Bottom region color extraction callback
 * @param retryCount Current retry count (internal)
 * @param signal Optional AbortSignal for cancellation
 */
export async function analyzeRegionsDominantColor(
  presignedUrl: string,
  topCallback: (color: string) => void,
  bottomCallback: (color: string) => void,
  retryCount: number = 0,
  signal?: AbortSignal,
): Promise<void> {
  // Check if already aborted
  if (signal?.aborted) {
    return;
  }

  // Browser compatibility check
  if (!isBrowserCompatible()) {
    topCallback(DEFAULT_COLOR);
    bottomCallback(DEFAULT_COLOR);
    return;
  }

  try {
    // Extract cache key
    const imageKey = extractS3KeyFromPresignedUrl(presignedUrl);
    const topCacheKey = `${imageKey}:top`;
    const bottomCacheKey = `${imageKey}:bottom`;

    // Check cache
    const cachedTop = colorCache.get(topCacheKey);
    const cachedBottom = colorCache.get(bottomCacheKey);

    if (cachedTop && cachedBottom) {
      topCallback(cachedTop);
      bottomCallback(cachedBottom);
      return;
    }

    // Check if analysis already in progress
    if (pendingAnalyses.has(imageKey)) {
      await pendingAnalyses.get(imageKey);
      const cachedTop = colorCache.get(topCacheKey);
      const cachedBottom = colorCache.get(bottomCacheKey);
      if (cachedTop && cachedBottom) {
        topCallback(cachedTop);
        bottomCallback(cachedBottom);
        return;
      }
    }

    // Create analysis promise
    const analysisPromise = (async () => {
      // 1st attempt: Direct image load (fast when CORS allowed)
      const tryDirectImageLoad = (): Promise<boolean> => {
        return new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          let resolved = false;

          const cleanup = () => {
            if (!resolved) {
              resolved = true;
              img.onload = null;
              img.onerror = null;
              img.src = '';
            }
          };

          // Handle abort
          if (signal) {
            signal.addEventListener(
              'abort',
              () => {
                cleanup();
                resolve(false);
              },
              { once: true },
            );
          }

          img.onload = () => {
            try {
              // 이미지 유효성 체크
              if (!img.complete || img.naturalWidth === 0 || img.naturalHeight === 0) {
                console.warn('Invalid image loaded');
                resolve(false);
                cleanup();
                return;
              }

              // ========== 상단 영역 분석 ==========
              const topCanvas = document.createElement('canvas');
              const topCtx = topCanvas.getContext('2d', { willReadFrequently: true });

              if (!topCtx) {
                resolve(false);
                cleanup();
                return;
              }

              const topStartY = Math.floor(img.height * REGION_CONFIG.TOP_START);
              const topEndY = Math.floor(img.height * REGION_CONFIG.TOP_END);
              const topHeight = topEndY - topStartY;

              if (topHeight <= 0 || img.width <= 0) {
                console.warn('Invalid top region dimensions');
                resolve(false);
                cleanup();
                return;
              }

              topCanvas.width = img.width;
              topCanvas.height = topHeight;
              topCtx.drawImage(img, 0, topStartY, img.width, topHeight, 0, 0, img.width, topHeight);

              const topColor = getDominantColorFromCanvas(topCanvas, topCtx);

              // ========== 하단 영역 분석 ==========
              const bottomCanvas = document.createElement('canvas');
              const bottomCtx = bottomCanvas.getContext('2d', { willReadFrequently: true });

              if (!bottomCtx) {
                resolve(false);
                cleanup();
                return;
              }

              const bottomStartY = Math.floor(img.height * REGION_CONFIG.BOTTOM_START);
              const bottomEndY = Math.floor(img.height * REGION_CONFIG.BOTTOM_END);
              const bottomHeight = bottomEndY - bottomStartY;

              if (bottomHeight <= 0) {
                console.warn('Invalid bottom region dimensions');
                resolve(false);
                cleanup();
                return;
              }

              bottomCanvas.width = img.width;
              bottomCanvas.height = bottomHeight;
              bottomCtx.drawImage(
                img,
                0,
                bottomStartY,
                img.width,
                bottomHeight,
                0,
                0,
                img.width,
                bottomHeight,
              );

              const bottomColor = getDominantColorFromCanvas(bottomCanvas, bottomCtx);

              // Cache and callback
              colorCache.set(topCacheKey, topColor);
              colorCache.set(bottomCacheKey, bottomColor);
              topCallback(topColor);
              bottomCallback(bottomColor);
              resolve(true);
            } catch (error) {
              console.error('Direct image load analysis error:', error);
              resolve(false);
            } finally {
              cleanup();
            }
          };

          img.onerror = (error) => {
            console.error('Image load error:', error);
            cleanup();
            resolve(false);
          };

          img.src = presignedUrl;
        });
      };

      // 2nd attempt: Fetch-based load (CORS fallback)
      const tryFetchImageLoad = async (): Promise<void> => {
        const response = await fetch(presignedUrl, {
          mode: 'cors',
          credentials: 'omit',
          signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status}`);
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);

        const img = new Image();
        let resolved = false;

        const cleanup = () => {
          if (!resolved) {
            resolved = true;
            img.onload = null;
            img.onerror = null;
            img.src = '';
            URL.revokeObjectURL(objectUrl);
          }
        };

        // Handle abort
        if (signal) {
          signal.addEventListener('abort', cleanup, { once: true });
        }

        img.onload = () => {
          try {
            // 이미지 유효성 체크
            if (!img.complete || img.naturalWidth === 0 || img.naturalHeight === 0) {
              throw new Error('Invalid fetched image');
            }

            // ========== 상단 영역 분석 ==========
            const topCanvas = document.createElement('canvas');
            const topCtx = topCanvas.getContext('2d', { willReadFrequently: true });

            if (!topCtx) {
              throw new Error('Canvas not available');
            }

            const topStartY = Math.floor(img.height * REGION_CONFIG.TOP_START);
            const topEndY = Math.floor(img.height * REGION_CONFIG.TOP_END);
            const topHeight = topEndY - topStartY;

            if (topHeight <= 0 || img.width <= 0) {
              throw new Error('Invalid top region dimensions');
            }

            topCanvas.width = img.width;
            topCanvas.height = topHeight;
            topCtx.drawImage(img, 0, topStartY, img.width, topHeight, 0, 0, img.width, topHeight);

            const topColor = getDominantColorFromCanvas(topCanvas, topCtx);

            // ========== 하단 영역 분석 ==========
            const bottomCanvas = document.createElement('canvas');
            const bottomCtx = bottomCanvas.getContext('2d', { willReadFrequently: true });

            if (!bottomCtx) {
              throw new Error('Canvas not available');
            }

            const bottomStartY = Math.floor(img.height * REGION_CONFIG.BOTTOM_START);
            const bottomEndY = Math.floor(img.height * REGION_CONFIG.BOTTOM_END);
            const bottomHeight = bottomEndY - bottomStartY;

            if (bottomHeight <= 0) {
              throw new Error('Invalid bottom region dimensions');
            }

            bottomCanvas.width = img.width;
            bottomCanvas.height = bottomHeight;
            bottomCtx.drawImage(
              img,
              0,
              bottomStartY,
              img.width,
              bottomHeight,
              0,
              0,
              img.width,
              bottomHeight,
            );

            const bottomColor = getDominantColorFromCanvas(bottomCanvas, bottomCtx);

            // Cache and callback
            colorCache.set(topCacheKey, topColor);
            colorCache.set(bottomCacheKey, bottomColor);
            topCallback(topColor);
            bottomCallback(bottomColor);
          } catch (error) {
            console.error('Fetch image analysis error:', error);
            if (retryCount >= MAX_RETRIES) {
              logAnalysisIssue('Color analysis failed after retries, using default color');
            }
            topCallback(DEFAULT_COLOR);
            bottomCallback(DEFAULT_COLOR);
          } finally {
            cleanup();
          }
        };

        img.onerror = (error) => {
          console.error('Fetched image load error:', error);
          if (retryCount >= MAX_RETRIES) {
            logAnalysisIssue('Image load failed after retries, using default color');
          }
          topCallback(DEFAULT_COLOR);
          bottomCallback(DEFAULT_COLOR);
          cleanup();
        };

        img.src = objectUrl;
      };

      // Execute two-tier approach
      const directSuccess = await tryDirectImageLoad();
      if (!directSuccess && !signal?.aborted) {
        await tryFetchImageLoad();
      }
    })();

    // Track pending analysis
    pendingAnalyses.set(imageKey, analysisPromise);

    try {
      await analysisPromise;
    } finally {
      pendingAnalyses.delete(imageKey);
    }
  } catch (error) {
    // Handle abort errors silently
    if (signal?.aborted || (error as Error).name === 'AbortError') {
      return;
    }

    // Retry on network errors
    if (retryCount < MAX_RETRIES && error instanceof TypeError) {
      setTimeout(() => {
        analyzeRegionsDominantColor(
          presignedUrl,
          topCallback,
          bottomCallback,
          retryCount + 1,
          signal,
        );
      }, RETRY_DELAY);
    } else {
      if (retryCount >= MAX_RETRIES) {
        logAnalysisIssue('Color analysis failed after all retries, using default color');
      }
      topCallback(DEFAULT_COLOR);
      bottomCallback(DEFAULT_COLOR);
    }
  }
}
/**
 * Analyze color from presigned URL image
 * Two-tier approach: Direct load → Fetch fallback
 * @param presignedUrl S3 presigned URL
 * @param callback Color extraction callback
 * @param retryCount Current retry count (internal)
 * @param signal Optional AbortSignal for cancellation
 */
export async function analyzePresignedImageColor(
  presignedUrl: string,
  callback: (color: string) => void,
  retryCount: number = 0,
  signal?: AbortSignal,
): Promise<void> {
  // Check if already aborted
  if (signal?.aborted) {
    return;
  }

  // Browser compatibility check
  if (!isBrowserCompatible()) {
    callback(DEFAULT_COLOR);
    return;
  }

  try {
    // Extract cache key
    const imageKey = extractS3KeyFromPresignedUrl(presignedUrl);

    // Check cache
    if (colorCache.has(imageKey)) {
      callback(colorCache.get(imageKey)!);
      return;
    }

    // Check if analysis already in progress
    if (pendingAnalyses.has(imageKey)) {
      await pendingAnalyses.get(imageKey);
      if (colorCache.has(imageKey)) {
        callback(colorCache.get(imageKey)!);
        return;
      }
    }

    // Create analysis promise
    const analysisPromise = (async () => {
      // 1st attempt: Direct image load (fast when CORS allowed)
      const tryDirectImageLoad = (): Promise<boolean> => {
        return new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          let resolved = false;

          const cleanup = () => {
            if (!resolved) {
              resolved = true;
              img.onload = null;
              img.onerror = null;
              img.src = '';
            }
          };

          // Handle abort
          if (signal) {
            signal.addEventListener(
              'abort',
              () => {
                cleanup();
                resolve(false);
              },
              { once: true },
            );
          }

          img.onload = () => {
            try {
              const colorThief: ColorThiefInstance = new ColorThief();
              const colorRgb: RGBColor = colorThief.getColor(img);
              const finalColor = rgbToHex(colorRgb);
              colorCache.set(imageKey, finalColor);
              callback(finalColor);
              resolve(true);
            } catch (error) {
              resolve(false);
            } finally {
              cleanup();
            }
          };

          img.onerror = () => {
            cleanup();
            resolve(false);
          };

          img.src = presignedUrl;
        });
      };

      // 2nd attempt: Fetch-based load (CORS fallback)
      const tryFetchImageLoad = async (): Promise<void> => {
        const response = await fetch(presignedUrl, {
          mode: 'cors',
          credentials: 'omit',
          signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status}`);
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);

        const img = new Image();
        let resolved = false;

        const cleanup = () => {
          if (!resolved) {
            resolved = true;
            img.onload = null;
            img.onerror = null;
            img.src = '';
            URL.revokeObjectURL(objectUrl);
          }
        };

        // Handle abort
        if (signal) {
          signal.addEventListener('abort', cleanup, { once: true });
        }

        img.onload = () => {
          try {
            const colorThief: ColorThiefInstance = new ColorThief();
            const colorRgb: RGBColor = colorThief.getColor(img);
            const finalColor = rgbToHex(colorRgb);
            colorCache.set(imageKey, finalColor);
            callback(finalColor);
          } catch (error) {
            if (retryCount >= MAX_RETRIES) {
              logAnalysisIssue('Color analysis failed after retries, using default color');
            }
            callback(DEFAULT_COLOR);
          } finally {
            cleanup();
          }
        };

        img.onerror = () => {
          if (retryCount >= MAX_RETRIES) {
            logAnalysisIssue('Image load failed after retries, using default color');
          }
          callback(DEFAULT_COLOR);
          cleanup();
        };

        img.src = objectUrl;
      };

      // Execute two-tier approach
      const directSuccess = await tryDirectImageLoad();
      if (!directSuccess && !signal?.aborted) {
        await tryFetchImageLoad();
      }
    })();

    // Track pending analysis
    pendingAnalyses.set(imageKey, analysisPromise);

    try {
      await analysisPromise;
    } finally {
      pendingAnalyses.delete(imageKey);
    }
  } catch (error) {
    // Handle abort errors silently
    if (signal?.aborted || (error as Error).name === 'AbortError') {
      return;
    }

    // Retry on network errors
    if (retryCount < MAX_RETRIES && error instanceof TypeError) {
      setTimeout(() => {
        analyzePresignedImageColor(presignedUrl, callback, retryCount + 1, signal);
      }, RETRY_DELAY);
    } else {
      if (retryCount >= MAX_RETRIES) {
        logAnalysisIssue('Color analysis failed after all retries, using default color');
      }
      callback(DEFAULT_COLOR);
    }
  }
}

/**
 * Analyze color from presigned URL video with regional analysis
 * Captures frame at specified time and analyzes top/bottom regions separately
 * @param presignedVideoUrl S3 presigned video URL
 * @param topCallback Top region color extraction callback
 * @param bottomCallback Bottom region color extraction callback
 * @param captureTime Time in seconds to capture frame (default: 1)
 * @param signal Optional AbortSignal for cancellation
 */
export async function analyzeRegionsDominantVideoColor(
  presignedVideoUrl: string,
  topCallback: (color: string) => void,
  bottomCallback: (color: string) => void,
  captureTime: number = 1,
  signal?: AbortSignal,
): Promise<void> {
  // Check if already aborted
  if (signal?.aborted) {
    return;
  }

  // Browser compatibility check
  if (!isBrowserCompatible()) {
    topCallback(DEFAULT_COLOR);
    bottomCallback(DEFAULT_COLOR);
    return;
  }

  // Validate captureTime
  if (captureTime < 0) {
    logAnalysisIssue('captureTime must be non-negative, using 0', LogLevel.WARN);
    captureTime = 0;
  }

  try {
    // Extract cache key
    const videoKey = extractS3KeyFromPresignedUrl(presignedVideoUrl);
    const topCacheKey = `${videoKey}:top`;
    const bottomCacheKey = `${videoKey}:bottom`;

    // Check cache
    const cachedTop = colorCache.get(topCacheKey);
    const cachedBottom = colorCache.get(bottomCacheKey);

    if (cachedTop && cachedBottom) {
      topCallback(cachedTop);
      bottomCallback(cachedBottom);
      return;
    }

    // Check if analysis already in progress
    if (pendingAnalyses.has(videoKey)) {
      await pendingAnalyses.get(videoKey);
      const cachedTop = colorCache.get(topCacheKey);
      const cachedBottom = colorCache.get(bottomCacheKey);
      if (cachedTop && cachedBottom) {
        topCallback(cachedTop);
        bottomCallback(cachedBottom);
        return;
      }
    }

    // Create analysis promise
    const analysisPromise = (async () => {
      let topCallbackCalled = false;
      let bottomCallbackCalled = false;

      const safeTopCallback = (color: string) => {
        if (!topCallbackCalled) {
          topCallbackCalled = true;
          topCallback(color);
        }
      };

      const safeBottomCallback = (color: string) => {
        if (!bottomCallbackCalled) {
          bottomCallbackCalled = true;
          bottomCallback(color);
        }
      };

      const response = await fetch(presignedVideoUrl, {
        mode: 'cors',
        credentials: 'omit',
        signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch video: ${response.status}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      const video = document.createElement('video');
      video.crossOrigin = 'anonymous'; // ✅ 추가
      video.preload = 'auto';

      const cleanup = () => {
        video.pause();
        video.removeAttribute('src'); // ✅ 이렇게 변경
        video.load();
        URL.revokeObjectURL(objectUrl);
      };

      // Handle abort
      if (signal) {
        signal.addEventListener('abort', cleanup, { once: true });
      }

      video.addEventListener(
        'loadeddata',
        async () => {
          try {
            // Set capture time
            video.currentTime = Math.min(captureTime, video.duration);

            // Wait for seek to complete
            await new Promise<void>((resolve) => {
              video.addEventListener('seeked', () => resolve(), { once: true });
            });

            // Optimize canvas size for color analysis
            const aspectRatio = video.videoWidth / video.videoHeight;
            let canvasWidth = Math.min(video.videoWidth, MAX_CANVAS_SIZE);
            let canvasHeight = canvasWidth / aspectRatio;

            if (canvasHeight > MAX_CANVAS_SIZE) {
              canvasHeight = MAX_CANVAS_SIZE;
              canvasWidth = canvasHeight * aspectRatio;
            }

            // ========== 상단 영역 분석 ==========
            const topCanvas = document.createElement('canvas');
            const topCtx = topCanvas.getContext('2d', { willReadFrequently: true });

            if (!topCtx) {
              throw new Error('Failed to get top canvas context');
            }

            const topStartY = Math.floor(canvasHeight * REGION_CONFIG.TOP_START);
            const topEndY = Math.floor(canvasHeight * REGION_CONFIG.TOP_END);
            const topHeight = topEndY - topStartY;

            if (topHeight > 0 && canvasWidth > 0) {
              topCanvas.width = canvasWidth;
              topCanvas.height = topHeight;

              // Draw video frame to canvas (top region only)
              topCtx.drawImage(
                video,
                0,
                topStartY * (video.videoHeight / canvasHeight), // 원본 비디오 좌표로 변환
                video.videoWidth,
                topHeight * (video.videoHeight / canvasHeight),
                0,
                0,
                canvasWidth,
                topHeight,
              );

              const topColor = getDominantColorFromCanvas(topCanvas, topCtx);
              colorCache.set(topCacheKey, topColor);
              safeTopCallback(topColor);
            } else {
              safeTopCallback(DEFAULT_COLOR);
            }

            // ========== 하단 영역 분석 ==========
            const bottomCanvas = document.createElement('canvas');
            const bottomCtx = bottomCanvas.getContext('2d', { willReadFrequently: true });

            if (!bottomCtx) {
              throw new Error('Failed to get bottom canvas context');
            }

            const bottomStartY = Math.floor(canvasHeight * REGION_CONFIG.BOTTOM_START);
            const bottomEndY = Math.floor(canvasHeight * REGION_CONFIG.BOTTOM_END);
            const bottomHeight = bottomEndY - bottomStartY;

            if (bottomHeight > 0 && canvasWidth > 0) {
              bottomCanvas.width = canvasWidth;
              bottomCanvas.height = bottomHeight;

              // Draw video frame to canvas (bottom region only)
              bottomCtx.drawImage(
                video,
                0,
                bottomStartY * (video.videoHeight / canvasHeight), // 원본 비디오 좌표로 변환
                video.videoWidth,
                bottomHeight * (video.videoHeight / canvasHeight),
                0,
                0,
                canvasWidth,
                bottomHeight,
              );

              const bottomColor = getDominantColorFromCanvas(bottomCanvas, bottomCtx);
              colorCache.set(bottomCacheKey, bottomColor);
              safeBottomCallback(bottomColor);
            } else {
              safeBottomCallback(DEFAULT_COLOR);
            }
          } catch (error) {
            logAnalysisIssue('Video frame capture error', LogLevel.ERROR);
            safeTopCallback(DEFAULT_COLOR);
            safeBottomCallback(DEFAULT_COLOR);
          } finally {
            cleanup();
          }
        },
        { once: true },
      );

      video.addEventListener(
        'error',
        (err) => {
          console.error('Video error event:', err);
          console.error('Video error details:', {
            error: video.error,
            code: video.error?.code,
            message: video.error?.message,
            networkState: video.networkState,
            readyState: video.readyState,
            src: video.src,
            currentSrc: video.currentSrc,
          });
          logAnalysisIssue(err.message, LogLevel.ERROR);
          safeTopCallback(DEFAULT_COLOR);
          safeBottomCallback(DEFAULT_COLOR);
          cleanup();
        },
        { once: true },
      );

      video.src = objectUrl;
    })();

    // Track pending analysis
    pendingAnalyses.set(videoKey, analysisPromise);

    try {
      await analysisPromise;
    } finally {
      pendingAnalyses.delete(videoKey);
    }
  } catch (error) {
    // Handle abort errors silently
    if (signal?.aborted || (error as Error).name === 'AbortError') {
      return;
    }

    logAnalysisIssue('Presigned video analysis failed', LogLevel.ERROR);
    topCallback(DEFAULT_COLOR);
    bottomCallback(DEFAULT_COLOR);
  }
}
// ============================================================================
// Video Color Analysis
// ============================================================================

/**
 * Analyze color from presigned URL video (captures frame at specified time)
 * @param presignedVideoUrl S3 presigned video URL
 * @param callback Color extraction callback
 * @param captureTime Time in seconds to capture frame (default: 1)
 * @param signal Optional AbortSignal for cancellation
 */
export async function analyzePresignedVideoColor(
  presignedVideoUrl: string,
  callback: (color: string) => void,
  captureTime: number = 1,
  signal?: AbortSignal,
): Promise<void> {
  // Check if already aborted
  if (signal?.aborted) {
    return;
  }

  // Browser compatibility check
  if (!isBrowserCompatible()) {
    callback(DEFAULT_COLOR);
    return;
  }

  // Validate captureTime
  if (captureTime < 0) {
    logAnalysisIssue('captureTime must be non-negative, using 0', LogLevel.WARN);
    captureTime = 0;
  }

  try {
    // Extract cache key
    const videoKey = extractS3KeyFromPresignedUrl(presignedVideoUrl);

    // Check cache
    if (colorCache.has(videoKey)) {
      callback(colorCache.get(videoKey)!);
      return;
    }

    // Check if analysis already in progress
    if (pendingAnalyses.has(videoKey)) {
      await pendingAnalyses.get(videoKey);
      if (colorCache.has(videoKey)) {
        callback(colorCache.get(videoKey)!);
        return;
      }
    }

    // Create analysis promise
    const analysisPromise = (async () => {
      let callbackCalled = false;
      const safeCallback = (color: string) => {
        if (!callbackCalled) {
          callbackCalled = true;
          callback(color);
        }
      };

      const response = await fetch(presignedVideoUrl, {
        mode: 'cors',
        credentials: 'omit',
        signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch video: ${response.status}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      const video = document.createElement('video');
      video.preload = 'auto';

      const cleanup = () => {
        video.pause();
        video.removeAttribute('src'); // 올바르게 src 제거
        video.load(); // Forces release of media resources
        URL.revokeObjectURL(objectUrl);
      };

      // Handle abort
      if (signal) {
        signal.addEventListener('abort', cleanup, { once: true });
      }

      video.addEventListener(
        'loadeddata',
        async () => {
          try {
            // Set capture time
            video.currentTime = Math.min(captureTime, video.duration);

            // Wait for seek to complete
            await new Promise<void>((resolve) => {
              video.addEventListener('seeked', () => resolve(), { once: true });
            });

            // Create canvas with optimized size
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (!ctx) {
              throw new Error('Failed to get canvas context');
            }

            // Optimize canvas size for color analysis
            const aspectRatio = video.videoWidth / video.videoHeight;
            let canvasWidth = Math.min(video.videoWidth, MAX_CANVAS_SIZE);
            let canvasHeight = canvasWidth / aspectRatio;

            if (canvasHeight > MAX_CANVAS_SIZE) {
              canvasHeight = MAX_CANVAS_SIZE;
              canvasWidth = canvasHeight * aspectRatio;
            }

            canvas.width = canvasWidth;
            canvas.height = canvasHeight;
            ctx.drawImage(video, 0, 0, canvasWidth, canvasHeight);

            // Use dataURL for simpler processing
            const dataUrl = canvas.toDataURL('image/jpeg', 0.8);

            const img = new Image();
            img.onload = () => {
              try {
                const colorThief: ColorThiefInstance = new ColorThief();
                const colorRgb: RGBColor = colorThief.getColor(img);
                const finalColor = rgbToHex(colorRgb);

                colorCache.set(videoKey, finalColor);
                safeCallback(finalColor);
              } catch (error) {
                logAnalysisIssue('ColorThief video analysis error', LogLevel.ERROR);
                safeCallback(DEFAULT_COLOR);
              }
            };

            img.onerror = () => {
              logAnalysisIssue('Video frame image load error', LogLevel.ERROR);
              safeCallback(DEFAULT_COLOR);
            };

            img.src = dataUrl;
          } catch (error) {
            logAnalysisIssue('Video frame capture error', LogLevel.ERROR);
            safeCallback(DEFAULT_COLOR);
          } finally {
            cleanup();
          }
        },
        { once: true },
      );

      video.addEventListener(
        'error',
        (err) => {
          console.error('[Video Load Error] Event:', err);
          console.error('[Video Load Error] Details:', {
            error: video.error,
            code: video.error?.code,
            message: video.error?.message,
            networkState: video.networkState,
            readyState: video.readyState,
            src: video.src,
            currentSrc: video.currentSrc,
            url: presignedVideoUrl,
          });
          logAnalysisIssue('Failed to load video', LogLevel.ERROR);
          safeCallback(DEFAULT_COLOR);
          cleanup();
        },
        { once: true },
      );

      video.src = objectUrl;
    })();

    // Track pending analysis
    pendingAnalyses.set(videoKey, analysisPromise);

    try {
      await analysisPromise;
    } finally {
      pendingAnalyses.delete(videoKey);
    }
  } catch (error) {
    // Handle abort errors silently
    if (signal?.aborted || (error as Error).name === 'AbortError') {
      return;
    }

    logAnalysisIssue('Presigned video analysis failed', LogLevel.ERROR);
    // Note: callback already wrapped in safeCallback inside analysisPromise
    callback(DEFAULT_COLOR);
  }
}

// ============================================================================
// Cache Management
// ============================================================================

/**
 * Clear color cache (memory management)
 */
export function clearColorCache(): void {
  colorCache.clear();
}

/**
 * Get cache size (debugging)
 */
export function getCacheSize(): number {
  return colorCache.size;
}

/**
 * Get number of pending analyses (debugging)
 */
export function getPendingAnalysesCount(): number {
  return pendingAnalyses.size;
}
